import { Alert, Button, Spinner, Switch, Table } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { C1ToAsrCallItem } from '@/entities/integrations';

import { useC1ToAsrCallColumns } from './useC1ToAsrCallColumns';

type Props = {
  calls: C1ToAsrCallItem[];
  /** Число найденных звонков — до применения заданного пользователем максимума */
  total: number;
  selectedIds: number[];
  isLoading: boolean;
  isError: boolean;
  /** Доступна ли отправка выбранных звонков текущему пользователю */
  canSend: boolean;
  isSending: boolean;
  /** Обрабатывать ли распознанную речь целиком; иначе — только шаги без LLM */
  isEnrichEnabled: boolean;
  onSelectionChange: (ids: number[]) => void;
  onEnrichChange: (isEnabled: boolean) => void;
  onSend: () => void;
};

/**
 * Результат отбора: таблица найденных звонков с множественным выбором,
 * выбором режима обработки и отправкой.
 */
export const C1ToAsrCallsResult: FC<Props> = ({
  calls,
  total,
  selectedIds,
  isLoading,
  isError,
  canSend,
  isSending,
  isEnrichEnabled,
  onSelectionChange,
  onEnrichChange,
  onSend,
}) => {
  const { t } = useTranslation(['integrations']);
  const columns = useC1ToAsrCallColumns();

  if (isLoading) return <Spinner className="size-6" />;

  if (isError) return <Alert type="error" title={t('integrations:c1ToAsr.searchError')} />;

  if (calls.length === 0) {
    return <div className="text-muted-foreground p-2">{t('integrations:c1ToAsr.table.empty')}</div>;
  }

  // Найденных больше, чем вернул сервер: отправить можно только загруженные звонки
  const isTruncated = total > calls.length;

  return (
    <div className="flex flex-col gap-3">
      {isTruncated && (
        <Alert
          type="warning"
          title={t('integrations:c1ToAsr.table.truncatedTitle', { shown: calls.length, total })}
          description={t('integrations:c1ToAsr.table.truncatedDescription')}
        />
      )}
      {/* Управление отправкой держим над таблицей: при большой выдаче кнопка под таблицей требует долгой прокрутки */}
      {canSend && (
        <div className="flex flex-col gap-3">
          <Switch
            value={isEnrichEnabled}
            onChange={onEnrichChange}
            label={t('integrations:c1ToAsr.enrich.label')}
            disabled={isSending}
          />
          <span className="text-muted-foreground text-sm">
            {isEnrichEnabled ? t('integrations:c1ToAsr.enrich.on') : t('integrations:c1ToAsr.enrich.off')}
          </span>
          <Button type="button" className="self-start" onClick={onSend} disabled={selectedIds.length === 0 || isSending}>
            {t('integrations:c1ToAsr.send')}
          </Button>
        </div>
      )}
      <span className="text-muted-foreground text-sm">
        {t('integrations:c1ToAsr.table.foundCount', { total, selected: selectedIds.length })}
      </span>
      <Table
        data={calls}
        columns={columns}
        getKey={(row) => row.id}
        selectedKeys={selectedIds}
        onSelect={({ selectedRows }) => onSelectionChange(selectedRows.map((row) => row.id))}
        enableMultiSelect
        styles={{ size: 'sm' }}
      />
    </div>
  );
};
